// Global toast utility to show toast notifications from non-React contexts
// This allows the service layer to trigger toasts when needed (e.g., 403 errors)

export type ToastSeverity = "success" | "info" | "warn" | "error";

export interface GlobalToastEvent {
  severity: ToastSeverity;
  summary: string;
  detail?: string;
  life?: number;
}

const DEFAULT_TOAST_LIFE = 5000;

/**
 * Triggers a global toast event that can be handled by React components
 * This is specifically designed for cases where toasts need to be shown
 * from contexts that don't have access to React (like service.ts)
 */
export const triggerGlobalToast = (options: GlobalToastEvent): void => {
  // Guard for non-browser contexts (SSR, Node tests)
  if (typeof window === "undefined") {
    return;
  }

  const toastEvent = new CustomEvent<GlobalToastEvent>("global-toast", {
    detail: {
      severity: options.severity,
      summary: options.summary,
      detail: options.detail ?? "",
      life: options.life ?? DEFAULT_TOAST_LIFE,
    },
  });

  window.dispatchEvent(toastEvent);
};

/**
 * Convenience function for 403 Forbidden errors
 */
export const triggerForbiddenToast = (message?: string): void => {
  triggerGlobalToast({
    severity: "error",
    summary: "Access Denied",
    detail: message || "You don't have permission to perform this action",
    life: 5000,
  });
};

/**
 * Convenience function for 500 Server errors
 */
export const triggerServerErrorToast = (message?: string): void => {
  triggerGlobalToast({
    severity: "error",
    summary: "Server Error",
    detail: message || "Something went wrong. Please try again later.",
    life: 5000,
  });
};

/**
 * Convenience function for connection errors (status 0)
 */
export const triggerConnectionErrorToast = (): void => {
  triggerGlobalToast({
    severity: "error",
    summary: "Connection Error",
    detail: "Unable to connect to server. Please check your connection.",
    life: 5000,
  });
};

/**
 * Convenience function for 408 Request Timeout errors
 */
export const triggerTimeoutToast = (): void => {
  triggerGlobalToast({
    severity: "warn",
    summary: "Request Timeout",
    detail: "The request took too long. Please try again.",
    life: 5000,
  });
};

/**
 * Convenience function for 429 Too Many Requests errors
 */
export const triggerRateLimitToast = (): void => {
  triggerGlobalToast({
    severity: "warn",
    summary: "Too Many Requests",
    detail: "Please wait a moment before trying again.",
    life: 5000,
  });
};

/**
 * Clears all currently visible global toasts
 */
export const clearGlobalToasts = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent("global-toast-clear"));
};
